<script setup lang="ts">
  import { createReusableTemplate } from '@vueuse/core'
  import { watch } from 'vue'
  import WrapperQC from '@qicgroupdoha/qic-connect-ds/components/WrapperQC'
  import IconQC from '@qicgroupdoha/qic-connect-ds/components/IconQC'
  import { TEST_ID, HEADER_TEST_ID, CONTENT_TEST_ID } from './AccordionQC.constants'

  interface Props {
    iconPosition?: 'start' | 'end'
    opened?: boolean
    iconColor?: string
    headerGap?: string
  }
  interface Emits {
    (e: 'toggle', isOpen: boolean): void
  }
  interface Slots {
    header(): void
    content(): void
  }
  const props = withDefaults(defineProps<Props>(), {
    iconPosition: 'start',
    opened: false,
    iconColor: 'var(--colorIcnSrfSecondary)',
    headerGap: 'var(--spacing-3)',
  })
  const emit = defineEmits<Emits>()
  const isOpen = defineModel<boolean>('isOpen', { default: false })

  defineSlots<Slots>()

  const [DefineHeaderIcon, ReuseHeaderIcon] = createReusableTemplate()

  const toggle = () => {
    if (props.opened) return
    isOpen.value = !isOpen.value
    emit('toggle', isOpen.value)
  }

  watch(
    () => props.opened,
    (newValue) => {
      isOpen.value = newValue
    },
    { immediate: true }
  )
</script>

<template>
  <DefineHeaderIcon>
    <IconQC class="accordion-qc__header-icon" name="chevron-down" :color="iconColor" />
  </DefineHeaderIcon>

  <WrapperQC class="accordion-qc" direction="column" v-bind="$attrs" :data-test="TEST_ID">
    <WrapperQC
      :data-test="HEADER_TEST_ID"
      class="accordion-qc__header"
      :class="{ 'is-open': isOpen, 'is-opened': opened }"
      :gap="headerGap"
      @click="toggle"
    >
      <ReuseHeaderIcon v-if="iconPosition === 'start' && !opened" />
      <slot name="header" />
      <ReuseHeaderIcon v-if="iconPosition === 'end' && !opened" />
    </WrapperQC>

    <Transition name="accordion">
      <div v-if="isOpen" class="accordion-qc__content" :data-test="CONTENT_TEST_ID">
        <slot name="content" />
      </div>
    </Transition>
  </WrapperQC>
</template>

<style lang="scss" scoped>
  .accordion-qc__header {
    align-items: center;
    cursor: pointer;

    &.is-open {
      .accordion-qc__header-icon {
        transform: rotate(180deg);
      }
    }

    &.is-opened {
      cursor: default;
    }
  }

  .accordion-qc__content {
    position: relative;
  }

  .accordion-qc__header-icon {
    transition: transform 0.15s ease;
  }
</style>
